import type { Vec2, Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import {
  horizontalCap,
  octahedronSolid,
  planPoint,
  prismSolid,
  pyramidOverRing,
  ringPoints,
} from '../solids';
import type { FlechePlan, TowerPlan } from './churchModel';
import type { SpireKind } from './churchParams';
import { pointedWindows, rectWall, type MasonryStyles } from './masonry';

export const TOWER = {
  belfryDrop: 1.2,
  belfryHeight: 6.5,
  /** The octagonal needle stands this share of the half-width inside the tower walls. */
  needleInset: 0.9,
  cornerPinnacle: { size: 0.9, rise: 3.5 },
  finial: 0.35,
  fleche: {
    radiusShare: 0.18,
    minRadius: 0.8,
    lanternRise: 3,
    needleRise: 8,
    pyramidRise: 3,
    openingShare: 0.6,
  },
} as const;

const OCTAGON_PHASE = Math.PI / 8;
/** Keeps corner pinnacles off the tower's top edge so each stays its own closed solid. */
const PINNACLE_CLEARANCE = 0.02;

/** Styles tagged for the painter view's vertical exaggeration, measured from `base`. */
export function tallStyles(styles: MasonryStyles, base: number): MasonryStyles {
  const tag = (s: VertexStyle): VertexStyle => ({ ...s, exaggeration: { base } });
  return { wall: tag(styles.wall), roof: tag(styles.roof), opening: tag(styles.opening) };
}

function squareRing(center: Vec2, half: number): Ring {
  return [
    [center[0] - half, center[1] - half],
    [center[0] + half, center[1] - half],
    [center[0] + half, center[1] + half],
    [center[0] - half, center[1] + half],
  ];
}

/** A square tower shaft from y0 to topY around `center`, crowned by `spire`. */
export interface SquareTower {
  readonly center: Vec2;
  readonly half: number;
  readonly y0: number;
  readonly topY: number;
  readonly spire: SpireKind;
  readonly spireHeight: number;
}

function cornerPinnacles(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, tower: SquareTower): void {
  const { size, rise } = TOWER.cornerPinnacle;
  const reach = tower.half - size / 2 - PINNACLE_CLEARANCE;
  for (const [su, sv] of [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ] as const) {
    const c: Vec2 = [tower.center[0] + su * reach, tower.center[1] + sv * reach];
    const ring = squareRing(c, size / 2);
    pyramidOverRing(builder, { roof: styles.wall, wall: styles.wall }, frame, ring, tower.topY, {
      plan: c,
      y: tower.topY + rise,
    });
  }
}

function buildSpire(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, tower: SquareTower): void {
  if (tower.spire === 'none' || tower.spireHeight <= 0) return;
  const { center } = tower;
  const apex = { plan: center, y: tower.topY + tower.spireHeight };
  const base =
    tower.spire === 'needle'
      ? ringPoints(center, (TOWER.needleInset * tower.half) / Math.cos(OCTAGON_PHASE), 8, OCTAGON_PHASE)
      : squareRing(center, tower.half);
  pyramidOverRing(builder, styles, frame, base, tower.topY, apex);
  if (tower.spire === 'needle') cornerPinnacles(builder, styles, frame, tower);
  octahedronSolid(builder, styles.wall, frame, planPoint(center[0], apex.y, center[1]), TOWER.finial);
}

/**
 * Square masonry tower with two belfry openings per face under a needle or pyramid,
 * tagged for the painter view's exaggeration from its own foot.
 */
export function buildSquareTower(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  tower: SquareTower
): void {
  const tall = tallStyles(styles, tower.y0);
  const ring = squareRing(tower.center, tower.half);
  const side = 2 * tower.half;
  const top = tower.topY - TOWER.belfryDrop;
  const sill = Math.max(tower.y0, top - TOWER.belfryHeight);
  const openings = pointedWindows(side, side / 2, sill - tower.y0, top - tower.y0);
  ring.forEach((from, i) => {
    rectWall(builder, tall, frame, [from, ring[(i + 1) % 4]!], [tower.y0, tower.topY], openings);
  });
  const pyramid = tower.spire === 'pyramid' && tower.spireHeight > 0;
  if (!pyramid) horizontalCap(builder, tall.wall, frame, ring, tower.topY, true);
  buildSpire(builder, tall, frame, tower);
}

/** Square west tower of a church, standing on the ground. */
export function buildWestTower(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, tower: TowerPlan): void {
  buildSquareTower(builder, styles, frame, {
    center: [(tower.u0 + tower.u1) / 2, tower.vc],
    half: tower.half,
    y0: 0,
    topY: tower.height,
    spire: tower.spire,
    spireHeight: tower.spireHeight,
  });
}

/** Slender octagonal roof turret (flèche) standing in the ridge, its foot hidden in the roof. */
export function buildFleche(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  fleche: FlechePlan,
  vesselHalf: number
): void {
  const f = TOWER.fleche;
  const tall = tallStyles(styles, fleche.footY);
  const radius = Math.max(f.minRadius, f.radiusShare * vesselHalf * 2);
  const center: Vec2 = [fleche.u, fleche.v];
  const ring = ringPoints(center, radius, 8, OCTAGON_PHASE);
  const lanternTop = fleche.footY + f.lanternRise + 1.5;
  prismSolid(builder, tall.wall, frame, ring, fleche.footY, lanternTop, { top: false, bottom: false });
  const rise = fleche.kind === 'needle' ? f.needleRise : f.pyramidRise;
  const apex = { plan: center, y: lanternTop + rise };
  pyramidOverRing(builder, tall, frame, ring, lanternTop, apex);
  octahedronSolid(builder, tall.wall, frame, planPoint(center[0], apex.y, center[1]), TOWER.finial);
}
